import * as React from "react";
import { useTranslation } from "react-i18next";
import { type OfflineTaskUpload } from "~/config/offline-storage/offlineTaskUploads.store";
import { Box } from "~/design-system/Box";
import { Stack } from "~/design-system/Stack";
import { Text } from "~/design-system/Text";

interface UploadsListProps {
  uploads: OfflineTaskUpload[];
  title: string;
  titleTone: "brandAccent" | "caution" | "critical" | "positive";
  maxItems?: number;
  children: (upload: OfflineTaskUpload, index: number) => React.ReactNode;
}

export const UploadsList: React.FC<UploadsListProps> = ({
  children,
  maxItems = 3,
  title,
  titleTone,
  uploads,
}) => {
  const { t } = useTranslation();

  if (uploads.length === 0) {
    return null;
  }

  const displayedUploads = uploads.slice(0, maxItems);
  const remainingCount = uploads.length - maxItems;

  return (
    <Box>
      <Text size="small" tone={titleTone} weight="medium">
        {title} ({uploads.length})
      </Text>
      <Box paddingTop="xxsmall">
        <Stack space="xxsmall">
          {displayedUploads.map((upload, index) => children(upload, index))}
          {remainingCount > 0 && (
            <Text size="xsmall" tone="neutral">
              +{remainingCount} more
            </Text>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
